import { Box, Stack, Table, TableCell, TableRow, styled } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { FunctionalAsset } from '@synergy/models';
import { PoolTypography } from '@synergy/components-next';
import { ReactElement } from 'react';
import { fields } from '@synergy/utils';

import { FunctionalAssetAutocomplete } from '../shared-components/functional-asset-autocomplete';
import { OrderDataFormValues } from '../../shopping-cart-page';
import { RowArticle } from './row-article';
import { RowSumSupplier } from './row-sum-supplier';
import { ShoppingCartSupplier } from '../../../../models/shopping-cart';
import { SupplierTableBody } from '../../../../components/supplier-table/supplier-table-body';
import { SupplierTableHead } from '../../../../components/supplier-table/supplier-table-head';
import { currencySelector } from '../../../../store/configuration-selectors';
import { updateFunctionalAsset } from '../../../../store/shopping-cart-actions';
import { useAppDispatch, useAppSelector } from '../../../../../store/store';

const StyledTable = styled(Table)(() => ({
    '& tr:last-child th, & tr:last-child td': {
        borderBottom: 'none',
    },
}));

export interface SupplierTableProps {
    supplier: ShoppingCartSupplier;
}

export const SupplierTableDesktop = (props: SupplierTableProps): ReactElement => {
    const { supplier } = props;
    const dispatch = useAppDispatch();
    const currency = useAppSelector(currencySelector);

    const fieldNames = fields<OrderDataFormValues>();

    const handleFunctionalAssetChanged = (value: FunctionalAsset | null): void => {
        if (value !== null) {
            dispatch(updateFunctionalAsset(supplier.id, value.id));
        }
    };

    return (
        <StyledTable>
            <SupplierTableHead>
                <TableRow>
                    <TableCell>
                        <Stack flexDirection="row" gap={2}>
                            <PoolTypography typography="subtitle1">
                                <strong>{supplier?.name}</strong>
                            </PoolTypography>
                        </Stack>
                    </TableCell>
                    <TableCell />
                    <TableCell align="right">
                        <FormattedMessage id="app.cart.supplier.singlePrice" />
                    </TableCell>
                    <TableCell align="right">
                        <FormattedMessage id="app.cart.supplier.pricePerArticle" />
                    </TableCell>
                </TableRow>
            </SupplierTableHead>
            {supplier.requireFunctionalAssets && (
                <TableRow sx={{ background: theme => theme.palette.grey[100] }}>
                    <TableCell>
                        <Stack flexDirection="row" gap={2}>
                            <Box sx={{ width: '110px', alignContent: 'center' }}>
                                <PoolTypography
                                    typography="body2"
                                    color="grey.800"
                                    translationId="app.cart.functionalAsset.label"
                                />
                            </Box>

                            <FunctionalAssetAutocomplete
                                name={fieldNames.functionalAssets + `[${supplier.id}]`}
                                supplierId={supplier.id}
                                required={true}
                                sx={{ width: '376px' }}
                                onValueChange={handleFunctionalAssetChanged}
                            />
                        </Stack>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                </TableRow>
            )}
            <SupplierTableBody>
                {supplier.items.map(item => {
                    return (
                        <RowArticle
                            cartItem={item}
                            key={item.product.id}
                            previewImage={item.previewImage?.['200x200'] ?? supplier.image?.['200x200']}
                        />
                    );
                })}
                <RowSumSupplier supplier={supplier} currency={currency} />
            </SupplierTableBody>
        </StyledTable>
    );
};
